// components/shared/CommentFileModal.tsx
import { Col, Row, Button, Input, Label, FormGroup } from 'reactstrap';
import SimpleModal from '../uiKits/modal/basicModal/SimpleModal';
import { Gallery, Item } from 'react-photoswipe-gallery';
import React, { Dispatch, RefObject, SetStateAction } from 'react';
import { getContrastColor, isNewPreview } from '../../utils';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { SelectOption, SingleValueContainer } from '../uiKits/chip/Chip';

/** ——— Shared preview types (export so parent can reuse) ——— */
export type PreviewBase = {
  url: string;
  name?: string;
  size?: number;
  type?: string;
  w?: number;
  h?: number;
};
export type NewPreview = { kind: 'new'; file: File } & PreviewBase;
export type ExistingPreview = { kind: 'existing' } & PreviewBase;
export type PreviewItem = NewPreview | ExistingPreview;

/** ——— Props ——— */
export interface CommentFileModalProps {
  isOpen: boolean;
  onClose: () => void;

  /** Labels allow i18n from parent (no hard dependency on t) */
  labels: {
    title: string; // e.g. t('leads.commentForm.label')
    placeholder: string; // e.g. t('leads.commentForm.placeholder')
    ok: string; // e.g. t('global.ok')
    saving: string; // e.g. t('global.saving')
    cancel: string; // e.g. t('global.cancel')
    remove?: string; // e.g. t('common.remove')
    examHandled?: string;
    yes?: string;
    no?: string;
    selectStatus?: string;
  };

  /** Comment input control */
  commentRef?: RefObject<HTMLTextAreaElement>;
  comment: string;
  onCommentChange: (value: string) => void;
  commentRequired?: boolean;

  /** File input control */
  hasFileInput?: boolean; // if false, file input won't be rendered and related validations won't apply
  fileInputRef?: RefObject<HTMLInputElement>;
  onFilesSelected?: React.ChangeEventHandler<HTMLInputElement>;

  /** Previews (existing and new) */
  previews?: PreviewItem[];
  onRemovePreview?: (p: PreviewItem) => void;

  /** UX */
  uploadErrors?: string[];
  submitting?: boolean;
  onSubmit: () => void;

  /** Optional: customize upload image placeholder */
  uploadHintImageSrc?: string; // defaults below

  isExamPassed?: boolean | null;
  setIsExamPassed?: Dispatch<SetStateAction<boolean | null>>;

  maxCommentLength?: number;
  filesRequired?: boolean;
  dropDown?: boolean;
  dropDownContent?: {
    id: number;
    statusName: string;
    colorCode: string;
    isSystem: boolean;
    source?: string;
  }[];
  dropDownPreSelected?: {
    id: number;
    statusName: string;
    colorCode: string;
    source?: string;
  } | null;
  dropDownSelectHandler?: (val: {
    id: number;
    statusName: string;
    colorCode: string;
    source?: string;
  }) => void;
  dropDownAddValueHandler?: (value: string) => void;
  addOptionHandler?: () => void;
  newOptionValue?: string;
  onNewOptionValueChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  isClearable?: boolean;
  handleStatusEdit?: (id: number, statusName: string, colorCode: string, source?: string) => void;
  handleStatusDelete?: (id: number, source?: string) => void;
  onFileDelete?: (preview: PreviewItem) => void;
}

const CommentFileModal: React.FC<CommentFileModalProps> = ({
  isOpen,
  onClose,
  labels,
  commentRef,
  comment,
  onCommentChange,
  fileInputRef,
  onFilesSelected,
  previews,
  onRemovePreview,
  uploadErrors = [],
  submitting = false,
  onSubmit,
  uploadHintImageSrc = '/assets/images/upload-images.png',
  isExamPassed,
  setIsExamPassed,
  maxCommentLength = 500,
  filesRequired = true,
  dropDown = false,
  dropDownContent,
  dropDownSelectHandler,
  dropDownAddValueHandler,
  dropDownPreSelected,
  addOptionHandler,
  isClearable = true,
  commentRequired = false,
  handleStatusEdit,
  handleStatusDelete,
  onFileDelete,
  hasFileInput = true,
}) => {
  const { t } = useTranslation();

  const dropdownOptions =
    dropDownContent?.map((item) => ({
      value: String(item.id),
      label: t(`global.selectorStatus.${item.statusName}`, {
        defaultValue: item.statusName,
      }),
      colorCode: item.colorCode,
      isSystem: item.isSystem,
      onEdit: () => {
        if (handleStatusEdit) {
          handleStatusEdit(item.id, item.statusName, item.colorCode, item.source);
        }
      },
      onDelete: () => {
        if (handleStatusDelete) {
          handleStatusDelete(item.id, item.source);
        }
      },
      source: item.source,
    })) ?? [];

  const colourStyles = {
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? state.data.colorCode || provided.backgroundColor
        : state.isFocused
          ? state.data.colorCode
            ? `${state.data.colorCode}90`
            : provided.backgroundColor
          : provided.backgroundColor,
      color: state.isSelected ? getContrastColor(state.data.colorCode) : provided.color,
    }),
    // Make the chip background a translucent variant of the color so the
    // small color swatch (rendered inside the chip) remains visible.
    SingleValue: (provided: any, state: any) => {
      const hex = state.data.colorCode;
      const luminance = hex
        ? (() => {
            try {
              const cleaned = hex.replace('#', '');
              const r = parseInt(cleaned.substring(0, 2), 16);
              const g = parseInt(cleaned.substring(2, 4), 16);
              const b = parseInt(cleaned.substring(4, 6), 16);
              return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            } catch (e) {
              return 1;
            }
          })()
        : 1;
      const borderColor = luminance > 0.6 ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.14)';
      return {
        ...provided,
        backgroundColor: hex ? `${hex}80` : provided.backgroundColor,
        border: `1px solid ${borderColor}`,
        boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.04)',
      };
    },

    control: (provided: any) => ({ ...provided, minHeight: '38px' }),
  };

  return (
    <SimpleModal isOpen={isOpen} close={onClose} centered>
      <div className="mt-4">
        {dropDown && (
          <div className="mt-3 mb-3 w-100">
            <Label className="w-full d-flex flex-column w-100">{labels.selectStatus}</Label>
            <div className="d-flex w-100 gap-2 align-items-center">
              <div style={{ flex: 1 }}>
                <Select
                  options={dropdownOptions}
                  // isSearchable={true}
                  styles={colourStyles}
                  className="custom-selector custom-scrollbar"
                  classNamePrefix="modal-select custom-selector"
                  isClearable={isClearable}
                  components={{
                    Option: SelectOption,
                    SingleValue: SingleValueContainer,
                    // Input: HiddenInput,
                  }}
                  value={
                    dropdownOptions.find(
                      (o) =>
                        o.value === (dropDownPreSelected ? String(dropDownPreSelected.id) : ''),
                    ) ?? null
                  }
                  onChange={(selected) => {
                    const selectedId = selected ? Number((selected as any).value) : NaN;
                    const selectedItem = dropDownContent?.find((item) => item.id === selectedId);
                    if (selectedItem && dropDownSelectHandler) {
                      dropDownSelectHandler({
                        id: selectedItem.id,
                        statusName: selectedItem.statusName,
                        colorCode: selectedItem.colorCode,
                      });
                    }
                  }}
                  formatOptionLabel={(option: any) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          borderRadius: 3,
                          backgroundColor: option.colorCode ? option.colorCode : '#eeeeee',
                          padding: '2px 6px',
                          color: getContrastColor(option.colorCode),
                        }}
                      >
                        {option.label}
                      </span>
                    </div>
                  )}
                  placeholder={labels.selectStatus}
                />
              </div>

              {addOptionHandler && (
                <Button onClick={addOptionHandler}>
                  <i className="icofont icofont-plus"></i>
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="position-relative d-flex flex-column">
          <label className="form-label fw-semibold">{labels.title}</label>

          <Input
            innerRef={commentRef as any}
            type="textarea"
            rows={5}
            placeholder={labels.placeholder}
            value={comment}
            onChange={(e) => {
              if (e.target.value.length <= maxCommentLength) onCommentChange(e.target.value);
            }}
            required={commentRequired}
          />
          <span
            className={`${comment.length >= maxCommentLength ? 'text-danger' : 'text-muted'} align-items-center small mt-2 ms-auto me-2`}
          >
            {comment.length}/{maxCommentLength}
          </span>
        </div>

        {setIsExamPassed && (
          <Row>
            <Col sm="auto" className="pt-2">
              <Label> {labels.examHandled}</Label>
              <div className="d-flex gap-3">
                <FormGroup check key={'1'} className={`radio radio-${'primary'}`}>
                  <Input
                    type="radio"
                    name={'ifpassed'}
                    id={`radio-ispassed`}
                    value={'true'}
                    checked={isExamPassed === true}
                    onChange={() => setIsExamPassed(true)}
                    required
                    // disabled={input.disabled}
                  />
                  <Label check htmlFor={`radio-ispassed`}>
                    {labels.yes}
                  </Label>
                </FormGroup>
                <FormGroup check key={'2'} className={`radio radio-primary`}>
                  <Input
                    type="radio"
                    name={'ifpassed'}
                    id={`radio-falsepassed`}
                    value={'false'}
                    checked={isExamPassed === false}
                    onChange={() => setIsExamPassed(false)}
                    required
                    // disabled={input.disabled}
                  />
                  <Label check htmlFor={`radio-falsepassed`}>
                    {labels.no}
                  </Label>
                </FormGroup>
                {/* {isExamPassed == null && (
                  <div className="text-danger small mt-1">{labels.examHandled} is required</div>
                )} */}
              </div>
            </Col>
          </Row>
        )}
        {hasFileInput && fileInputRef && (
          <>
            {/* Upload control */}
            <div className="d-flex align-items-center gap-2 mt-3">
              <Input
                innerRef={fileInputRef as any}
                id="screenshots"
                type="file"
                accept="image/*"
                multiple
                onChange={onFilesSelected}
                hidden
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                style={{ width: 60, height: 60, padding: 0 }}
                color="white"
              >
                <img
                  src={uploadHintImageSrc}
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 6,
                    display: 'block',
                  }}
                  alt="upload files"
                />
              </Button>
            </div>

            {uploadErrors.length > 0 && (
              <ul className="mt-2 mb-0 small text-danger">
                {uploadErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}

            {/* Thumbnails + PhotoSwipe fullscreen */}
            {!!previews?.length && (
              <Gallery withCaption>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {previews.map((p, idx) => (
                    <figure key={`${p.url}-${idx}`} className="m-0 position-relative">
                      <Item
                        original={p.url}
                        width={p.w ?? 1600}
                        height={p.h ?? 1200}
                        caption={p.name}
                      >
                        {({ ref, open }: any) => (
                          // eslint-disable-next-line jsx-a11y/anchor-is-valid
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              open();
                            }}
                          >
                            <img
                              ref={ref as React.Ref<HTMLImageElement>}
                              src={p.url}
                              alt={p.name ?? 'preview'}
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 6,
                                display: 'block',
                              }}
                            />
                          </a>
                        )}
                      </Item>
                      {onFileDelete && p.kind === 'existing' && (
                        <Button
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-1 p-1"
                          onClick={() => {
                            onFileDelete(p);
                            // onRemovePreview(p);
                          }}
                          title={labels.remove || 'Remove'}
                        >
                          ×
                        </Button>
                      )}
                      {/* Remove only for NEW (unsaved) files */}
                      {isNewPreview(p) && onRemovePreview && (
                        <Button
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 m-1 p-1"
                          onClick={() => onRemovePreview(p)}
                          title={labels.remove || 'Remove'}
                        >
                          ×
                        </Button>
                      )}
                    </figure>
                  ))}
                </div>
              </Gallery>
            )}
          </>
        )}
        <Row className="mt-3 gap-2">
          <Col sm="auto">
            <Button
              color="primary"
              disabled={
                submitting ||
                (!!setIsExamPassed && isExamPassed == null) ||
                (!previews?.length && filesRequired) ||
                (commentRequired && comment.trim() === '')
              }
              onClick={onSubmit}
            >
              {submitting ? labels.saving : labels.ok}
            </Button>
          </Col>
          <Col sm="auto">
            <Button color="secondary" disabled={submitting} onClick={onClose}>
              {labels.cancel}
            </Button>
          </Col>
        </Row>
      </div>
    </SimpleModal>
  );
};

export default CommentFileModal;
